import React, { useCallback, useEffect, useState } from 'react';
import Switch from '../../../../components/Switch';
import ConfirmModal from '../../../../components/ui/ConfirmModal';
import { NativeSelect } from '../../../../components/ui/Field';
import { attentionService } from '../../../../services/attentionService';
import { MODES, ROLES, teamWarnings } from '../../../../utils/attention';
import { toastError, toastSuccess } from '../../../../utils/toast';
import { useAuth } from '../../../../context/AuthContext';
import AreasEditor from './AreasEditor';

// Atención humana del proyecto (scripts 52 y 55): modo (clásico o con asignación), áreas y rol y área de cada usuario. Se guarda al
// momento, aparte del botón del proyecto, como Avisos de IA. Los usuarios se dan de alta en Clientes; aquí solo se elige su rol en este
// proyecto.
export default function AttentionConfig({ projectId }) {
  const { isAdmin } = useAuth();
  const [mode, setMode] = useState('classic');
  const [team, setTeam] = useState([]);
  const [areas, setAreas] = useState(null);     // null = sin el script 55 (la parte de áreas no se muestra)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);       // cliente_id que se está guardando
  const [pendingMode, setPendingMode] = useState(null);

  const reload = useCallback(async () => {
    try {
      const r = await attentionService.load(projectId);
      setMode(r.mode); setTeam(r.team); setAreas(r.areas); setError('');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { if (projectId && isAdmin) reload(); }, [projectId, isAdmin, reload]);

  if (!isAdmin) return <p className="text-sm text-fg-dim">Solo un administrador de la plataforma configura la atención humana.</p>;
  if (!projectId) return <p className="text-sm text-fg-dim">Guarda el proyecto primero; después podrás configurar su atención humana.</p>;
  if (loading) return <p className="text-sm text-fg-faint">Cargando…</p>;
  if (error) return <p role="alert" className="text-sm text-signal">{error}</p>;

  const saveMember = async (m, patch) => {
    setBusy(m.cliente_id);
    try {
      await attentionService.setMember(projectId, m.cliente_id, { role: m.role, isActive: m.is_active, ...patch });
      await reload();
      toastSuccess(`${m.full_name || 'Usuario'}: guardado`);
    } catch (e) { toastError(e.message); }
    finally { setBusy(null); }
  };

  // Cambios de áreas: se guardan y se recarga todo (los usuarios de un área borrada quedan sin área)
  const areaAction = (fn, ok) => async (...args) => {
    try { await fn(...args); await reload(); toastSuccess(ok); } catch (e) { toastError(e.message); }
  };
  const areaHandlers = {
    onCreate: areaAction((name) => attentionService.createArea(projectId, name), 'Área agregada'),
    onRename: areaAction((id, name) => attentionService.updateArea(id, { name: name.trim() }), 'Área renombrada'),
    onToggle: areaAction((id, isActive) => attentionService.updateArea(id, { is_active: isActive }), 'Área guardada'),
    onDelete: async (id) => { await attentionService.deleteArea(id); await reload(); toastSuccess('Área borrada'); },
  };

  const warnings = teamWarnings(mode, team);

  return (
    <div className="space-y-6">
      <fieldset>
        <legend className="text-sm font-semibold text-fg mb-2">Modo</legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Object.entries(MODES).map(([key, def]) => {
            const checked = mode === key;
            return (
              <label key={key} className={`relative cursor-pointer rounded-2xl p-4 ring-1 transition ${checked ? 'ring-fg/40 bg-raised' : 'ring-line hover:ring-fg/20'}`}>
                <input type="radio" name={`attention-mode-${projectId}`} value={key} checked={checked} className="sr-only"
                  onChange={() => { if (!checked) setPendingMode(key); }} />
                <span className="block text-sm font-semibold text-fg">{def.label}{checked && <span className="ml-2 text-xs font-normal text-live">actual</span>}</span>
                <span className="block text-[13px] text-fg-dim mt-1">{def.help}</span>
              </label>
            );
          })}
        </div>
      </fieldset>

      {areas && <AreasEditor areas={areas} team={team} {...areaHandlers} />}

      <div>
        <p className="text-sm font-semibold text-fg">Equipo del proyecto</p>
        <p className="text-[13px] text-fg-dim mt-0.5 mb-3">
          Los usuarios se dan de alta en Clientes. Quien no tenga rol elegido cuenta como administrador del proyecto, así nadie pierde acceso.
          {mode === 'classic' && ' Los roles se aplican cuando el proyecto esté en modo con asignación.'}
        </p>
        {warnings.map((w) => <p key={w} className="text-[13px] text-caution mb-2">{w}</p>)}
        {team.length > 0 && (
          <ul className="divide-y divide-line rounded-2xl ring-1 ring-line">
            {team.map((m) => {
              const id = `att-${m.cliente_id}`;
              return (
                <li key={m.cliente_id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                  <div className="min-w-[10rem] flex-1">
                    <p className="text-sm text-fg">{m.full_name || 'Sin nombre'}</p>
                    <p className="text-xs text-fg-faint">{Number(m.open_chats) === 1 ? '1 chat abierto' : `${m.open_chats ?? 0} chats abiertos`}</p>
                  </div>
                  <NativeSelect aria-label={`Rol de ${m.full_name || 'este usuario'}`} className="!w-auto" value={m.role} disabled={busy === m.cliente_id}
                    title={ROLES[m.role]?.help} onChange={(e) => saveMember(m, { role: e.target.value })}>
                    {Object.entries(ROLES).map(([key, r]) => <option key={key} value={key}>{r.label}</option>)}
                  </NativeSelect>
                  {areas && m.role === 'attendant' && (
                    <NativeSelect aria-label={`Área de ${m.full_name || 'este usuario'}`} className="!w-auto" value={m.area_id ?? ''} disabled={busy === m.cliente_id}
                      onChange={(e) => saveMember(m, { areaId: e.target.value ? Number(e.target.value) : null })}>
                      <option value="">Sin área</option>
                      {areas.map((a) => <option key={a.id} value={a.id}>{a.name}{a.is_active ? '' : ' (apagada)'}</option>)}
                    </NativeSelect>
                  )}
                  <div className="flex items-center gap-2">
                    <Switch id={id} size="sm" checked={m.is_active} disabled={busy === m.cliente_id} label={`${m.full_name || 'Usuario'} recibe chats`}
                      onChange={(v) => saveMember(m, { isActive: v })} />
                    <label htmlFor={id} className="text-xs text-fg-dim cursor-pointer">Recibe chats</label>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <ConfirmModal
        open={Boolean(pendingMode)} onClose={() => setPendingMode(null)}
        title={pendingMode === 'assigned' ? 'Activar la asignación' : 'Volver al modo clásico'}
        confirmLabel={pendingMode === 'assigned' ? 'Activar asignación' : 'Volver a clásico'}
        onConfirm={async () => { await attentionService.setMode(projectId, pendingMode); await reload(); toastSuccess('Modo de atención guardado'); }}
      >
        {pendingMode === 'assigned' ? (
          <>
            <p>Desde el siguiente chat que el agente pase a una persona, ese chat tendrá un responsable y solo él recibirá el aviso.</p>
            <p>Los usuarios de nivel 2 verán en Chats solo los suyos. Revisa los roles del equipo antes de activarlo.</p>
          </>
        ) : (
          <p>Se vuelve a avisar a todo el equipo y todos ven todos los chats, como antes. Los roles se conservan por si lo activas de nuevo.</p>
        )}
      </ConfirmModal>
    </div>
  );
}
